import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import InformesBackButton from '../../components/shared/InformesBackButton';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import carteraService, { SaldoCartera } from '../../services/carteraService';
import { formatCurrency } from '../../utils/formatters';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';

interface DisponibilidadesFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  tipoCuenta?: string;
}

const TIPO_CUENTA_TODOS = 'Todos';

const Disponibilidades: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const loadStored = () =>
    loadTesoreriaFiltros<DisponibilidadesFiltrosStored>(TESORERIA_FILTROS_KEYS.disponibilidades);

  const [saldos, setSaldos] = useState<SaldoCartera[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const [totalCount, setTotalCount] = useState(0);
  const [_totalPages, setTotalPages] = useState(0);
  const [tipoCuenta, setTipoCuenta] = useState(() =>
    storedString(loadStored(), 'tipoCuenta', TIPO_CUENTA_TODOS)
  );

  const tipoCuentaApi = (tipo?: string) => {
    const value = tipo !== undefined ? tipo : tipoCuenta;
    return value === TIPO_CUENTA_TODOS ? undefined : value;
  };

  const cargarSaldos = async (pageNumber: number = page + 1, pageSize: number = rowsPerPage, search?: string, tipo?: string) => {
    try {
      setLoading(true);
      const response = await carteraService.getSaldos({
        pageNumber,
        pageSize,
        searchTerm: search !== undefined ? search : (searchTerm.trim() || undefined),
        tipoDeCuenta: tipoCuentaApi(tipo),
      });
      setSaldos(response.items || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 0);
    } catch (error: any) {
      console.error('Error al cargar saldos:', error);
      enqueueSnackbar('Error al cargar los saldos de las carteras', { variant: 'error' });
      setSaldos([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.disponibilidades,
    { searchTerm, rowsPerPage, tipoCuenta },
    [searchTerm, rowsPerPage, tipoCuenta]
  );

  // Debounce para la búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      cargarSaldos(1, rowsPerPage, searchTerm, tipoCuenta);
      setPage(0);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, rowsPerPage, tipoCuenta]);

  // Cargar cuando cambia la página
  useEffect(() => {
    if (page >= 0) {
      cargarSaldos(page + 1, rowsPerPage, searchTerm, tipoCuenta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalSaldo = useMemo(() => {
    return saldos.reduce((sum, saldo) => sum + (saldo.saldo || 0), 0);
  }, [saldos]);

  const saldosPositivos = useMemo(() => {
    return saldos.filter((s) => (s.saldo || 0) >= 0).length;
  }, [saldos]);

  const saldosNegativos = useMemo(() => {
    return saldos.filter((s) => (s.saldo || 0) < 0).length;
  }, [saldos]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleTipoCuentaChange = (event: React.SyntheticEvent, newValue: string) => {
    setTipoCuenta(newValue);
    setPage(0);
  };

  const handleRowClick = (saldo: SaldoCartera) => {
    navigate(`/tesoreria/informes/movimientos-carteras?nroBanco=${saldo.nroBanco}`);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <InformesBackButton to="/tesoreria/informes" useBrowserBack />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2',
                }}
              >
                Disponibilidades
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => cargarSaldos(page + 1, rowsPerPage, searchTerm, tipoCuenta)}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
              }}
            >
              Actualizar
            </Button>
          </Stack>

          {/* Resumen de saldos */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalSaldo)}
                </Typography>
                <Typography variant="caption">Saldo Total</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#4caf50', color: '#fff', textAlign: 'center', py: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {saldosPositivos}
                </Typography>
                <Typography variant="caption">Carteras con Saldo Positivo</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#f44336', color: '#fff', textAlign: 'center', py: 2 }}>
                <TrendingDownIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {saldosNegativos}
                </Typography>
                <Typography variant="caption">Carteras con Saldo Negativo</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ bgcolor: '#757575', color: '#fff', textAlign: 'center', py: 2 }}>
                <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {totalCount}
                </Typography>
                <Typography variant="caption">Total de Carteras</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs para filtrar por tipo de cuenta */}
          <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tipoCuenta}
              onChange={handleTipoCuentaChange}
              aria-label="tipo de cuenta tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minHeight: 48,
                },
              }}
            >
              <Tab label="Todos" value={TIPO_CUENTA_TODOS} />
              <Tab label="Cuenta Corriente" value="Cuenta Corriente" />
              <Tab label="Caja de Ahorro" value="Caja de Ahorro" />
              <Tab label="Cartera" value="Cartera" />
            </Tabs>
          </Box>

          {/* Búsqueda */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por banco, número, tipo de cuenta, CBU, titular..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabla de saldos */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {isMobile ? (
                <Stack spacing={2}>
                  {saldos.map((saldo) => {
                    const esPositivo = (saldo.saldo || 0) >= 0;
                    return (
                      <Card
                        key={saldo.nroBanco}
                        onClick={() => handleRowClick(saldo)}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            transform: 'translateY(-2px)',
                            transition: 'all 0.2s ease-in-out',
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {saldo.nombreBanco?.trim() || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Nro. Banco: {saldo.nroBanco}
                                </Typography>
                              </Box>
                              <Chip
                                label={formatCurrency(saldo.saldo || 0)}
                                sx={{
                                  bgcolor: esPositivo ? '#4caf50' : '#f44336',
                                  color: '#fff',
                                  fontWeight: 600,
                                }}
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Tipo de Cuenta:</strong> {saldo.tipoDeCuenta?.trim() || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Nro. de Cuenta:</strong> {saldo.nroDeCuenta?.trim() || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>CBU:</strong> {saldo.cbu?.trim() || '-'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Titular:</strong> {saldo.titular?.trim() || '-'}
                            </Typography>
                            {saldo.sucursal && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Sucursal:</strong> {saldo.sucursal.trim()}
                              </Typography>
                            )}
                            {saldo.nroCtaCble && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Nro. Cta. Contable:</strong> {saldo.nroCtaCble}
                              </Typography>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {saldos.length === 0 && !loading && (
                    <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                      {searchTerm ? 'No se encontraron carteras con ese criterio' : 'No hay saldos disponibles'}
                    </Typography>
                  )}
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                  />
                </Stack>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    boxShadow: 'none',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1.25,
                        fontSize: '0.8125rem',
                        lineHeight: 1.2,
                      },
                      '& .MuiTableCell-head': {
                        py: 0.75,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Nro. Banco</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Nombre Banco</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tipo de Cuenta</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Nro. de Cuenta</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>CBU</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Titular</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Nro. Cta. Contable</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Saldo
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {saldos.map((saldo) => {
                        const esPositivo = (saldo.saldo || 0) >= 0;
                        return (
                          <TableRow
                            key={saldo.nroBanco}
                            onClick={() => handleRowClick(saldo)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: '#fafafa',
                              },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{saldo.nroBanco}</TableCell>
                            <TableCell>{saldo.nombreBanco?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.tipoDeCuenta?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.nroDeCuenta?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.cbu?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.titular?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.sucursal?.trim() || '-'}</TableCell>
                            <TableCell>{saldo.nroCtaCble || '-'}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color: esPositivo ? '#4caf50' : '#f44336',
                              }}
                            >
                              {formatCurrency(saldo.saldo || 0)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {saldos.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                              {searchTerm
                                ? 'No se encontraron carteras con ese criterio'
                                : 'No hay saldos disponibles'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Disponibilidades;

